import { getQuestionGroupId } from 'components-machine/src/selects';
import React from 'react';
import { createPortal } from 'react-dom';
import type { FINAL_CLICK_PAGE, Group, Poll, Question, Welcome } from 'schema/poll';
import { ICState } from 'schema/state';
import { Question as WebviewQuestion } from 'schema/webview';

type Resource = Welcome | Question | WebviewQuestion | Group | FINAL_CLICK_PAGE;

const getResources = (poll: Poll) => [...(poll.welcome ?? []), ...poll.questions, ...poll.groups, ...poll.finals];

const getCurrentResource = (resources: Resource[], state: ICState, poll: Poll) => {
  const currentResourceId = state.welcomeId || state.questionId || state.groupId || state.finalId;

  const currentResourceIndex = resources.findIndex(r =>
    state.groupId && 'groupId' in r
      ? getQuestionGroupId(r, poll.questionDecks) === currentResourceId
      : r.id === currentResourceId,
  );

  return { currentResourceId, currentResourceIndex };
};

const getResourceImages = (resource: Resource, poll: Poll) => {
  const getResource = (resource: Extract<Resource, { data: { type: string } }>) => {
    const { data } = resource;

    const images: string[] = [];

    if ('image' in data && data.image?.src) images.push(data.image.src);
    if ('answers' in data && data.answers.length) {
      images.push(
        ...data.answers.reduce<string[]>((result, answer) => {
          if ('image' in answer && answer.image?.src) result.push(answer.image.src);
          if ('feedbackImage' in answer && answer.feedbackImage?.src) result.push(answer.feedbackImage.src);

          return result;
        }, []),
      );
    }

    return images;
  };

  const isGroupResource = (resource: Resource): resource is Group => !('type' in resource.data);

  if (isGroupResource(resource)) {
    const groupQuestions = poll.questions.filter(
      question => getQuestionGroupId(question, poll.questionDecks) === resource.id,
    );
    return [...groupQuestions.reduce<string[]>((result, resource) => [...result, ...getResource(resource)], [])];
  }

  return getResource(resource);
};

const getResourceImagesList = ({
  resources,
  currentResourceId,
  poll,
}: {
  resources: Resource[];
  currentResourceId: string;
  poll: Poll;
}) => {
  const resourceSrcToPriorityMap = resources.reduce<Record<string, 'high' | 'low'>>((result, resource) => {
    const { id: resourceId } = resource;
    const priority: 'high' | 'low' = resourceId === currentResourceId ? 'high' : 'low';

    const images = getResourceImages(resource, poll);

    for (const src of images) {
      const existingSrcPriority = result[src];

      if (!existingSrcPriority || priority === 'high') {
        result[src] = priority;
      }
    }

    return result;
  }, {});

  return Object.entries(resourceSrcToPriorityMap).map(([src, priority]) => ({ src, priority }));
};

export const getPrefetchTags = (poll: Poll, state: ICState) => {
  const resources = getResources(poll);

  const { currentResourceId, currentResourceIndex } = getCurrentResource(resources, state, poll);

  if (!currentResourceId) return null;

  const nextResources = resources.slice(currentResourceIndex);

  const imagesList = getResourceImagesList({ resources: nextResources, currentResourceId, poll });

  return (
    <>
      {imagesList.map(({ priority, src }) => (
        <link key={src} rel={priority === 'high' ? 'preload' : 'prefetch'} as="image" href={src} />
      ))}
    </>
  );
};

export function PrefetchTags({ poll, icState }: { poll: Poll; icState: ICState }) {
  const prefetchTags = getPrefetchTags(poll, icState);
  return createPortal(prefetchTags, document.querySelector('head')!);
}
